import { useStableRest } from "../../../../utils/use-stable-rest";
import {
  CLEAR_FILTERS_LABEL,
  CLEAR_SEARCH_LABEL,
  LIST_EMPTY_ILLUSTRATION,
  listEmptyTexts,
  pluralOf,
} from "./messages";
import type { ListEmptyKind } from "./messages";
import type {
  ListEmptyStateProps,
  ListEmptyStateViewModel,
  UseListEmptyStateOptions,
  UseListEmptyStateResult,
} from "./types-list";

/**
 * Busca tem precedência sobre filtros (os dois ativos ainda são a tela de busca vazia, como
 * `resolveEmptyStateKind` das telas de hoje).
 */
export function resolveListEmptyKind(hasSearch: boolean, hasFilters: boolean): ListEmptyKind {
  if (hasSearch) return "sem-resultados-busca";
  if (hasFilters) return "sem-resultados-filtros";
  return "sem-registros";
}

/** Deriva o estado vazio de uma listagem: `isEmpty` só com a consulta concluída e sem registros. */
export function useListEmptyState({
  status,
  total,
  search,
  filtersCount,
}: UseListEmptyStateOptions): UseListEmptyStateResult {
  return {
    isEmpty: status === "success" && total === 0,
    kind: resolveListEmptyKind(search.trim() !== "", filtersCount > 0),
  };
}

// `useListEmptyState` é o hook público de dados; por isso o VM desta parte tem outro nome.
/** ViewModel do `ListEmptyState`: escolhe ilustração e textos pelo `kind`; "Limpar" só com busca ou filtros e `onClear`. */
export function useList({
  noun,
  gender,
  plural,
  title,
  description,
  hasSearch,
  hasFilters,
  onClear,
  clearTestId,
  className,
  classNames,
  testId,
  ...props
}: ListEmptyStateProps): ListEmptyStateViewModel {
  const rest = useStableRest(props);
  const kind = resolveListEmptyKind(hasSearch, hasFilters);
  const texts = listEmptyTexts(kind, noun, gender, pluralOf(noun, plural));
  const canClear = kind !== "sem-registros" && onClear;
  const clearLabel = kind === "sem-resultados-busca" ? CLEAR_SEARCH_LABEL : CLEAR_FILTERS_LABEL;

  return {
    illustration: LIST_EMPTY_ILLUSTRATION[kind],
    title: title || texts.title,
    description: description || texts.description,
    clearLabel: canClear ? clearLabel : undefined,
    onClear,
    clearTestId,
    testId,
    className,
    classNames,
    rest,
  };
}
